import type { Role } from '@/lib/constants/roles';
import type { AdminPermissionKey } from '@/lib/constants/admin-permissions';
import type { ActiveMembership, SessionUser } from '@/lib/types/auth.types';

/**
 * Backend claim shape reflected on every session-issuing response and
 * `/auth/me` (login returns `sub`, `/auth/me` returns `id`). Single source
 * for the raw-to-`SessionUser` mapping - previously duplicated between
 * `auth.api.ts` and `invites.api.ts`, which is how `needsTermsReconsent`
 * (MYN-33) was silently dropped on one path while being added to the other.
 */
export interface RawSessionUser {
  sub?: string;
  id?: string;
  email: string;
  roles: Role[];
  activeMembership?: ActiveMembership | null;
  tier: 'free' | 'paid';
  isWorkEmail: boolean;
  needsTermsReconsent?: boolean;
  /** `/auth/me` only (MYN-144): the admin role's keys, resolved live. */
  adminPermissions?: AdminPermissionKey[];
  /** `/auth/me` in a view-as tab only (MYN-335): who is viewing and until when. */
  viewAs?: { adminName: string; expiresAt: string } | null;
}

export function toSessionUser(raw: RawSessionUser): SessionUser {
  return {
    id: raw.sub ?? raw.id ?? '',
    email: raw.email,
    roles: raw.roles,
    activeMembership: raw.activeMembership ?? null,
    tier: raw.tier,
    isWorkEmail: raw.isWorkEmail,
    needsTermsReconsent: raw.needsTermsReconsent,
    adminPermissions: raw.adminPermissions,
    viewAs: raw.viewAs,
  };
}
